import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Zap, Wallet, Wrench, LineChart, Factory, Building2,
  Snowflake, ShoppingCart, Users, Baby, Cross, UtensilsCrossed, Landmark,
  CheckCircle2, ShieldCheck, Sparkles, ArrowUpRight,
} from "lucide-react";
import CtaBanner from "@/components/site/CtaBanner";
import { Button } from "@/components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1768677432858-b9a8608dd849?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwcm9vZnRvcCUyMHNvbGFyJTIwcGFuZWxzfGVufDB8fHx8MTc4MzU3Nzg1Nnww&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  { icon: Wallet, title: "No upfront capital", body: "We fund, install and own the system so there’s no purchase cost on your balance sheet." },
  { icon: Zap, title: "Solar + battery, on your site", body: "Cleaner on-site energy designed around your load profile — daytime and beyond." },
  { icon: LineChart, title: "Simple flat kWh rate", body: "Pay only for the energy you use from the system, at an agreed cents-per-kWh rate." },
  { icon: Wrench, title: "Monitoring & maintenance", body: "We look after the asset for the full term. Your business keeps operating as normal." },
];

const STEPS = [
  { n: "01", title: "Business energy review", body: "Share bills, site details and business info. We assess load, roof and operating profile." },
  { n: "02", title: "System design", body: "We size solar and battery around your load profile, roof space and network constraints." },
  { n: "03", title: "PPA proposal", body: "You receive an indicative usage-based energy proposal with estimated size, rate and potential savings." },
  { n: "04", title: "Installation & commissioning", body: "Once approved, we arrange installation, grid connection and commissioning." },
  { n: "05", title: "Pay for energy used", body: "You pay a flat cents-per-kWh rate for the energy consumed from the on-site system." },
  { n: "06", title: "Ongoing support", body: "We monitor, maintain and support the system for the life of the agreement." },
];

const INDUSTRIES = [
  { icon: Factory, label: "Manufacturing" },
  { icon: Building2, label: "Warehousing" },
  { icon: Snowflake, label: "Cold storage" },
  { icon: ShoppingCart, label: "Supermarkets" },
  { icon: Users, label: "Clubs & community" },
  { icon: Baby, label: "Childcare & education" },
  { icon: Cross, label: "Medical & healthcare" },
  { icon: UtensilsCrossed, label: "Hospitality" },
  { icon: Landmark, label: "Commercial property" },
];

const WHY = [
  "No upfront system purchase",
  "Reduce exposure to rising grid electricity costs",
  "Use cleaner energy on-site",
  "Simple usage-based billing",
  "System ownership and maintenance handled by Be Green Group",
];

export default function Home() {
  return (
    <div>
      {/* HERO — asymmetric, left aligned */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="container-x mx-auto max-w-7xl pt-14 md:pt-24 pb-16 md:pb-28 grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-mist border border-black/5 rounded-full px-3.5 py-1.5 text-[12.5px] font-medium text-forest mb-6"
            >
              <span className="h-2 w-2 rounded-full bg-bright" />
              Commercial Solar + Battery PPA · Australia
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.05 }}
              className="font-heading text-[40px] leading-[1.03] sm:text-5xl lg:text-6xl font-semibold text-forest tracking-tight"
              data-testid="hero-headline"
            >
              Commercial solar and battery <br className="hidden md:block" />
              <span className="text-forest">with </span>
              <span className="relative inline-block">
                no upfront cost
                <span className="absolute left-0 right-0 -bottom-1 h-2 rounded-full bg-bright/40" />
              </span>
              .
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.15 }}
              className="mt-6 text-neutral-600 text-[17px] md:text-lg leading-relaxed max-w-xl"
            >
              Be Green Group helps Australian businesses access cleaner, cheaper on-site energy
              through a simple usage-based plan. We install, own and maintain the system — you only
              pay for the energy your business uses.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-9 flex flex-wrap gap-3"
            >
              <Link to="/apply" data-testid="hero-primary-cta">
                <Button className="rounded-full h-12 px-6 bg-bright hover:bg-bright/90 text-[hsl(150_40%_8%)] font-semibold text-[15px]">
                  Check My Business Eligibility <ArrowRight size={18} className="ml-1" />
                </Button>
              </Link>
              <Link to="/how-it-works" data-testid="hero-secondary-cta">
                <Button variant="outline" className="rounded-full h-12 px-6 border-forest/20 text-forest hover:bg-mist font-semibold">
                  See how it works
                </Button>
              </Link>
            </motion.div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-[13.5px] text-neutral-600">
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-bright"/> No upfront system purchase</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-bright"/> Keep your existing retailer</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-bright"/> Ownership & maintenance handled</span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
          >
            <div className="absolute -inset-6 rounded-[2.2rem] bg-mist -z-10" />
            <img
              src={HERO_IMG}
              alt="Commercial rooftop solar installation"
              className="w-full h-[420px] md:h-[520px] object-cover rounded-3xl shadow-xl"
            />
            <div className="absolute -bottom-6 -left-6 md:-left-10 bg-white border border-black/5 rounded-2xl shadow-lg p-5 w-[74%] md:w-[280px]">
              <div className="flex items-center justify-between">
                <span className="text-[11px] tracking-widest uppercase font-semibold text-neutral-500">Your PPA rate</span>
                <ShieldCheck size={16} className="text-forest" />
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-heading text-3xl md:text-4xl font-semibold text-forest">c / kWh</span>
              </div>
              <p className="mt-2 text-[12.5px] text-neutral-500 leading-snug">
                Flat, usage-based. Indicative rate confirmed after site assessment.
              </p>
            </div>
            <div className="absolute -top-5 right-5 md:right-8 bg-forest text-white rounded-2xl px-4 py-3 shadow-lg">
              <p className="text-[11px] tracking-widest uppercase text-white/70">Owned & maintained</p>
              <p className="font-heading text-lg font-semibold leading-tight mt-1">by Be Green Group</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PILLARS — bento asymmetric */}
      <section className="bg-mist py-20 md:py-28" data-testid="home-pillars">
        <div className="container-x mx-auto max-w-7xl">
          <div className="max-w-2xl mb-14">
            <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-3">A better way to power your business</p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-forest leading-[1.05]">
              Cleaner on-site energy, without buying the system.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-white border border-black/5 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300"
                data-testid={`home-pillar-${i}`}
              >
                <span className="inline-grid place-items-center h-10 w-10 rounded-xl bg-mist text-forest">
                  <p.icon size={18} strokeWidth={2.2} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-semibold text-forest">{p.title}</h3>
                <p className="mt-2 text-[14.5px] text-neutral-600 leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="container-x mx-auto max-w-7xl py-20 md:py-28" data-testid="home-steps">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-start">
          <div>
            <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-3">How it works</p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-forest leading-[1.05]">
              A simple six-step business energy plan.
            </h2>
            <p className="mt-5 text-neutral-600 text-[16px] max-w-md leading-relaxed">
              From energy review through installation and long-term support, we take care of the
              system so your team can focus on running the business.
            </p>
            <Link to="/how-it-works" className="mt-8 inline-flex items-center gap-2 text-forest font-semibold group">
              Read the full process
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-2xl border border-black/5 bg-white p-6 hover:border-forest/20 transition-colors"
              >
                <span className="text-bright font-heading text-sm font-semibold tracking-widest">{s.n}</span>
                <h3 className="mt-2 font-heading text-lg font-semibold text-forest">{s.title}</h3>
                <p className="mt-2 text-[14.5px] text-neutral-600 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="relative overflow-hidden bg-forest text-white py-20 md:py-28 grain" data-testid="home-industries">
        <div className="container-x mx-auto max-w-7xl">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-bright text-xs tracking-[0.18em] uppercase font-semibold mb-3">Who this is for</p>
              <h2 className="font-heading text-3xl md:text-5xl font-semibold leading-[1.05] max-w-3xl">
                Built for high-usage, long-term commercial operators.
              </h2>
            </div>
            <Link to="/industries" className="text-white/80 hover:text-white inline-flex items-center gap-1 font-semibold">
              Explore industries <ArrowUpRight size={18}/>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {INDUSTRIES.map((it, i) => (
              <div
                key={it.label}
                data-testid={`industry-${i}`}
                className="rounded-2xl bg-white/[0.06] border border-white/10 p-5 hover:bg-white/[0.09] transition-colors"
              >
                <it.icon size={22} className="text-bright" strokeWidth={1.8} />
                <p className="mt-6 font-heading font-semibold text-[15.5px]">{it.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="container-x mx-auto max-w-7xl py-20 md:py-28" data-testid="home-why">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-14 items-center">
          <div className="order-2 lg:order-1 rounded-3xl bg-mist p-8 md:p-10 border border-black/5">
            <Sparkles size={22} className="text-bright" />
            <h3 className="mt-4 font-heading text-2xl md:text-3xl font-semibold text-forest leading-tight">
              Designed for CFOs, operators & property owners.
            </h3>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">
              A usage-based energy plan keeps the capital cost off your balance sheet and shifts
              the responsibility of owning and running the asset to us. Your business keeps its
              existing electricity retailer for grid supply — we bill only for on-site energy used.
            </p>
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-3">Why businesses choose Be Green Group</p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-forest leading-[1.05]">A cleaner energy plan, without the CapEx.</h2>
            <ul className="mt-8 space-y-4">
              {WHY.map((w) => (
                <li key={w} className="flex items-start gap-3 text-[15.5px] text-neutral-700">
                  <span className="mt-0.5 inline-grid place-items-center h-6 w-6 rounded-full bg-bright/15 text-forest">
                    <CheckCircle2 size={14} strokeWidth={2.4} />
                  </span>
                  {w}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <CtaBanner testId="home-cta" />
    </div>
  );
}
